<template>
	<div id="wrapper">
		<SidebarView :memacc="true" :memaccVisible="true" :interestPay="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header">
							<h6 class="m-0 fw-bold text-green">Pay Interest</h6>
						</div>
						<div class="card-body">
							<p class="small text-muted mb-3">
								<strong>Balance basis</strong> in a period: Completed Deposits − Completed Withdrawals.
								Posting: member receives a <strong>Deposit</strong>; GL: <strong>DR</strong> selected Cost-of-Funds COA,
								<strong>CR</strong> product COA.
							</p>

							<div class="row g-3">
								<!-- Manual run -->
								<div class="col-12">
									<div class="border rounded p-3">
										<div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
											<h6 class="m-0 fw-bold text-green">Manual run</h6>
										</div>

										<div class="row g-2">
											<div class="col-md-6">
												<label class="akkurate-dark small">Interest setup <span class="text-danger">*</span></label>
												<multiselect
													v-model="manual.setup"
													:options="setupOptions"
													label="name"
													track-by="id"
													placeholder="Select setup"
													class="field-container"
												/>
											</div>
											<div class="col-md-3">
												<label class="akkurate-dark small">Period start <span class="text-danger">*</span></label>
												<b-form-input v-model="manual.period_start" type="date" class="field-container" />
											</div>
											<div class="col-md-3">
												<label class="akkurate-dark small">Period end <span class="text-danger">*</span></label>
												<b-form-input v-model="manual.period_end" type="date" class="field-container" />
											</div>
											<div class="col-12">
												<label class="akkurate-dark small">Branches (optional)</label>
												<multiselect
													v-model="manual.branches"
													:options="branches"
													label="name"
													track-by="id"
													:multiple="true"
													placeholder="All branches"
													class="field-container"
												/>
											</div>

											<div class="col-12 d-flex justify-content-end gap-2">
												<b-button
													class="akkurate-green-btn btn-size text-white"
													:disabled="manualRunning || !canPayInterest"
													@click="runManual"
												>
													<b-spinner v-if="manualRunning" small class="me-1" />
													Run
												</b-button>
											</div>
										</div>

										<div v-if="manualError" class="alert alert-danger py-2 small mt-2 mb-0">
											{{ manualError }}
										</div>

										<div v-if="manualResult" class="alert alert-success py-2 small mt-2 mb-0">
											<div class="d-flex justify-content-between flex-wrap gap-2">
												<div>
													<strong>Run started.</strong>
													<span v-if="manualResult.id">Run ID: {{ manualResult.id }}</span>
												</div>
												<b-button
													size="sm"
													variant="outline-success"
													:disabled="!manualResult.id || !manual.setup"
													@click="openPayments(manual.setup.id, manualResult.id)"
												>
													View payments
												</b-button>
											</div>
										</div>
									</div>
								</div>

								<!-- Automated run -->
								<div class="col-12">
									<div class="border rounded p-3">
										<div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
											<h6 class="m-0 fw-bold text-green">Automate</h6>
										</div>

										<div class="row g-2 align-items-end">
											<div class="col-md-4">
												<label class="akkurate-dark small">Frequency <span class="text-danger">*</span></label>
												<b-form-select v-model="auto.frequency" :options="frequencyOptions" class="field-container" />
											</div>
											<div class="col-md-4">
												<label class="akkurate-dark small">Date (optional)</label>
												<b-form-input v-model="auto.date" type="date" class="field-container" />
											</div>
											<div class="col-md-auto">
												<b-button
													class="akkurate-green-btn btn-size text-white"
													:disabled="autoRunning || !canPayInterest"
													@click="startAutoRun"
												>
													<b-spinner v-if="autoRunning" small class="me-1" />
													Start
												</b-button>
											</div>
										</div>

										<div v-if="job.job_id" class="mt-3 small">
											<div class="d-flex flex-wrap gap-2 align-items-center">
												<span class="badge" :class="jobBadge(job.status)">{{ job.status || "PENDING" }}</span>
												<span><strong>Job ID:</strong> {{ job.job_id }}</span>
												<span v-if="job.message" class="text-muted">{{ job.message }}</span>
												<b-button
													size="sm"
													variant="outline-secondary"
													:disabled="!isPolling"
													@click="stopPolling"
												>
													Stop polling
												</b-button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>

		<!-- Payments modal -->
		<b-modal v-model="showPaymentsModal" size="xxl" title="Run payments" @hidden="resetPayments">
			<b-overlay :show="paymentsBusy" rounded="sm">
				<b-table
					striped
					bordered
					responsive
					:items="paymentRows"
					:fields="paymentFields"
					show-empty
					class="table_text_size"
				/>
				<div v-if="paymentsMeta && paymentsMeta.last_page > 1" class="d-flex justify-content-end">
					<b-pagination
						v-model="paymentsPage"
						:total-rows="paymentsMeta.total || 0"
						:per-page="paymentsMeta.per_page || 15"
						@change="loadPayments"
					/>
				</div>
			</b-overlay>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" @click="showPaymentsModal = false">Close</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";
import { hasPermission } from "@/helper";

export default {
	name: "InterestPay",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	data() {
		return {
			setupOptions: [],
			branches: [],

			manual: {
				setup: null,
				period_start: "",
				period_end: "",
				branches: [],
			},
			manualRunning: false,
			manualError: "",
			manualResult: null,

			auto: {
				frequency: "monthly",
				date: "",
			},
			autoRunning: false,
			job: {
				job_id: "",
				status: "",
				message: "",
			},
			isPolling: false,
			pollTimer: null,

			showPaymentsModal: false,
			paymentsBusy: false,
			paymentRows: [],
			paymentsMeta: null,
			paymentsPage: 1,
			paymentsContext: { setupId: null, runId: null },
			paymentFields: [
				{ key: "id", label: "#" },
				{ key: "member_code", label: "Member" },
				{ key: "account_number", label: "Account" },
				{ key: "amount", label: "Amount" },
				{ key: "status", label: "Status" },
				{ key: "created_at", label: "Created" },
			],

			frequencyOptions: [
				{ value: "daily", text: "Daily" },
				{ value: "weekly", text: "Weekly" },
				{ value: "monthly", text: "Monthly" },
				{ value: "quarterly", text: "Quarterly" },
				{ value: "annually", text: "Annually" },
			],
		};
	},
	computed: {
		canPayInterest() {
			return hasPermission("interest_management_pay");
		},
	},
	created() {
		this.loadDropdowns();
	},
	beforeDestroy() {
		this.stopPolling();
	},
	methods: {
		async loadDropdowns() {
			try {
				const [dd, br] = await Promise.all([
					ApiService.get("/interest-setups/dropdown"),
					ApiService.get("/settings/branches/dropdown").catch(() => ({ data: {} })),
				]);
				const d = dd.data?.data ?? dd.data ?? {};
				this.setupOptions = d.data || d.setups || d || [];
				this.branches = br.data?.data || br.data?.branches || br.data || [];
			} catch (e) {
				/* non-blocking */
			}
		},
		validateManual() {
			if (!this.manual.setup?.id) return "Please select an interest setup.";
			if (!this.manual.period_start) return "Please select period start.";
			if (!this.manual.period_end) return "Please select period end.";
			return "";
		},
		async runManual() {
			this.manualError = "";
			this.manualResult = null;
			const err = this.validateManual();
			if (err) {
				this.manualError = err;
				return;
			}
			this.manualRunning = true;
			this.$Progress.start();
			try {
				const payload = {
					period_start: this.manual.period_start,
					period_end: this.manual.period_end,
					branch_ids:
						this.manual.branches && this.manual.branches.length
							? this.manual.branches.map((b) => b.id)
							: [],
				};
				const res = await ApiService.post(
					`/interest-setups/${this.manual.setup.id}/run`,
					payload,
				);
				this.manualResult = res.data?.data || res.data || {};
				toast.fire({ icon: "success", title: res.data?.message || "Run started" });
			} catch (e) {
				this.manualError = e.response?.data?.message || "Run failed.";
			} finally {
				this.manualRunning = false;
				this.$Progress.finish();
			}
		},
		async startAutoRun() {
			this.autoRunning = true;
			this.job = { job_id: "", status: "", message: "" };
			this.$Progress.start();
			try {
				const body = {
					frequency: this.auto.frequency,
					date: this.auto.date || undefined,
				};
				const res = await ApiService.post("/interest/run", body);
				const jobId = res.data?.job_id || res.data?.data?.job_id;
				this.job.job_id = jobId || "";
				this.job.status = res.data?.status || "ok";
				this.job.message = "Job started. Polling status…";
				if (this.job.job_id) {
					this.startPolling();
				}
				toast.fire({ icon: "success", title: "Background run started" });
			} catch (e) {
				toast.fire({ icon: "error", title: e.response?.data?.message || "Could not start job" });
			} finally {
				this.autoRunning = false;
				this.$Progress.finish();
			}
		},
		startPolling() {
			if (this.isPolling) return;
			this.isPolling = true;
			this.fetchJobStatus();
			this.pollTimer = setInterval(() => {
				this.fetchJobStatus();
			}, 2000);
		},
		stopPolling() {
			this.isPolling = false;
			if (this.pollTimer) {
				clearInterval(this.pollTimer);
				this.pollTimer = null;
			}
		},
		async fetchJobStatus() {
			if (!this.job.job_id) return;
			try {
				const res = await ApiService.get(`/transactions/job-status/${this.job.job_id}`);
				const data = res.data || {};
				this.job.status = data.status || this.job.status;
				this.job.message = data.message || this.job.message;
				const s = (this.job.status || "").toLowerCase();
				if (["completed", "failed"].includes(s)) {
					this.stopPolling();
				}
			} catch (e) {
				// stop polling on not found
				if (e.response && e.response.status === 404) {
					this.stopPolling();
					this.job.message = "Job not found.";
				}
			}
		},
		jobBadge(status) {
			const s = (status || "").toLowerCase();
			if (s === "completed") return "bg-success";
			if (s === "failed") return "bg-danger";
			return "bg-warning text-dark";
		},

		async openPayments(setupId, runId) {
			this.paymentsContext = { setupId, runId };
			this.showPaymentsModal = true;
			await this.loadPayments(1);
		},
		resetPayments() {
			this.paymentsContext = { setupId: null, runId: null };
			this.paymentRows = [];
			this.paymentsMeta = null;
			this.paymentsPage = 1;
			this.paymentsBusy = false;
		},
		async loadPayments(page) {
			const { setupId, runId } = this.paymentsContext;
			if (!setupId || !runId) return;
			this.paymentsBusy = true;
			try {
				const p = page || 1;
				const res = await ApiService.get(
					`/interest-setups/${setupId}/runs/${runId}/payments`,
					{ params: { page: p, per_page: 15 } },
				);
				const body = res.data;
				this.paymentRows = body.data || body || [];
				this.paymentsMeta = body.meta || null;
				this.paymentsPage = p;
			} catch (e) {
				this.paymentRows = [];
				this.paymentsMeta = null;
			} finally {
				this.paymentsBusy = false;
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

